export type Instance = {
  id: string;
  name: string;
  status: string;
  canClaim: boolean;
  canUndo: boolean;
  settled: boolean;
  isRequired: boolean;
  weight: number;
  childName?: string;
};

const DONE = new Set(["approved", "claimed"]);

/* One job. The whole row is the tap target -- a four-year-old should not have
 * to hit a checkbox. Claimed-but-unreviewed reads as done to the child, with a
 * quieter mark, because from their side the work IS finished.
 *
 * Three things can be true of a row and only one of them is about the status:
 * whether it is DONE, whether the child may still change it, and whether a
 * grown-up has answered it. A square a grown-up has settled is deliberately
 * not just inert -- a button that ignores a tap teaches a child the app is
 * broken, so it says who closed it and why. */
export function ChoreRow({
  instance,
  onToggle,
  busy,
}: {
  instance: Instance;
  onToggle?: (i: Instance) => void;
  busy?: boolean;
}) {
  const done = DONE.has(instance.status);
  const awaiting = instance.status === "claimed";
  const excused = instance.status === "excused";
  const interactive =
    Boolean(onToggle) && (instance.canClaim || instance.canUndo);

  const meta: string[] = [];
  if (instance.childName) meta.push(instance.childName);
  if (excused) meta.push("a grown-up said you don't need to do this one");
  else if (instance.settled && done) meta.push("a grown-up said yes ✓");
  else if (awaiting) meta.push("waiting for a grown-up");
  else if (instance.status === "missed") meta.push("missed");

  /* Settled and excused are both finished, and neither is a failure, so they
     share the calm end of the palette rather than the red one. Excused keeps
     its own quieter colour: it is off the list, but it is not a star earned,
     and drawing it as one would tell the child they did something they did
     not do. */
  const tone = excused
    ? { bg: "var(--paper)", edge: "var(--ink-3)", ink: "var(--ink-2)" }
    : done
      ? { bg: "var(--save-soft)", edge: "var(--save)", ink: "var(--save-deep)" }
      : { bg: "var(--paper)", edge: "var(--paper-edge)", ink: "var(--ink)" };

  return (
    <button
      onClick={() => interactive && onToggle?.(instance)}
      disabled={!interactive || busy}
      aria-pressed={done}
      title={
        instance.settled && !interactive
          ? "A grown-up has answered this one."
          : undefined
      }
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        textAlign: "left",
        padding: "12px 16px",
        background: tone.bg,
        border: `2px solid ${tone.edge}`,
        boxShadow: "none",
        borderRadius: "var(--r-md)",
        opacity: !interactive && !done && !excused ? 0.7 : 1,
      }}
    >
      {excused ? <Excused /> : <Star filled={done} muted={awaiting} />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontFamily: "var(--font-display)",
            fontSize: 17,
            fontWeight: 500,
            color: tone.ink,
          }}
        >
          {instance.name}
        </span>
        {meta.length > 0 && (
          /* One meta line, joined. Two sibling inline spans ran together as
             "Emmawaiting for a grown-up" in the parent's review queue. */
          <span
            style={{ display: "block", fontSize: 12.5, color: "var(--ink-3)" }}
          >
            {meta.join(", ")}
          </span>
        )}
      </span>
      {instance.weight > 1 && (
        <span
          className="money"
          title={`Counts as ${instance.weight} jobs`}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "var(--ink-3)",
            border: "1.5px solid var(--paper-edge)",
            borderRadius: 999,
            padding: "2px 8px",
          }}
        >
          ×{instance.weight}
        </span>
      )}
    </button>
  );
}

/* A reward star is a sticker, so it is drawn as one: die-cut, with the paper
 * keyline a sticker keeps whatever it was stuck on, and lifted off the row.
 * Claimed-but-unreviewed gets a paler gold and no lift -- pressed on, not yet
 * pressed down. The empty state stays a plain outline: a star has ten corners
 * and a dashed one breaks at every single of them, which at 30px reads as a
 * smudge rather than as the place a sticker goes. */
function Star({ filled, muted }: { filled: boolean; muted?: boolean }) {
  const star =
    "M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.5l1.1-6.5L2.6 9.4l6.5-.9z";

  if (!filled) {
    return (
      <svg
        className="sticker-empty"
        width="30"
        height="30"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d={star}
          fill="none"
          stroke="var(--ink-3)"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      className="sticker"
      width="30"
      height="30"
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={muted ? { filter: "none" } : undefined}
    >
      <path
        d={star}
        fill={muted ? "var(--star-muted)" : "var(--star)"}
        stroke="var(--sticker-edge)"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Not a star and not a cross. The same dash the week's grid uses for an
   excused square, so a child reading their own row and a child looking at the
   chart are being told the same thing by the same mark. */
function Excused() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12" cy="12" r="9.2"
        fill="none" stroke="var(--ink-3)" strokeWidth="1.8"
        strokeDasharray="3 2.6"
      />
      <path
        d="M7.8 12h8.4"
        stroke="var(--ink-3)" strokeWidth="2.2" strokeLinecap="round"
      />
    </svg>
  );
}
